import React, {
  Fragment, useState, useEffect,
} from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import InventoryLocationActions from '@Actions/settings/inventoryLocationActions';
import Helpers from '@Apis/helpers';
import H3Image from '@Uilib/h3Image';
import searchIcon from '@Images/icons/icon-search.png';
import {
  Breadcrumb, Checkbox, Drawer, Empty,
} from 'antd';
import H3Button, { defaultButtonTypes } from '@Uilib/h3Button';
import SelectDepartment from '@Components/Common/SelectDepartment';
import H3Text from '@Uilib/h3Text';
import closeIcon from '@Images/icons/icon-close-blue.png';
import H3FormInput from '@Uilib/h3FormInput';
import './style.scss';
import { EditOutlined, HomeFilled } from '@ant-design/icons';

/**
 * @param status
 */
function WarehouseSetup(
  {
    createInventoryLocation, updateInventoryLocation, getInventoryLocations, user, history, departments,
    getInventoryLocationsLoading, createInventoryLocationLoading, updateInventoryLocationLoading, deleteInventoryLocationLoading, inventoryLocations,
  },
) {
  const [searchKeyword, setSearchKeyword] = useState('');
  const [keyword, setKeyword] = useState('');
  const [sectionName, setSectionName] = useState('');
  const [sectionActive, setSectionActive] = useState(true);
  const [sectionStorable, setSectionStorable] = useState(true);
  const [showNewLocationDrawer, setShowNewLocationDrawer] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [parentLocationId, setParentLocationId] = useState(null);
  const [tenantDepartmentId, setTenantDepartmentId] = useState('');
  const [tenantDepartmentName, setTenantDepartmentName] = useState('');

  /**
   *
   * @param department
   * @param keyword
   */
  const pushParamsToUrl = (depId, locId, key) => {
    let url = '/settings/warehouse-setup?';
    if (tenantDepartmentId) url += `tenantDepartmentId=${depId}&`;
    if (parentLocationId) url += `parentLocationId=${locId}&`;
    if (keyword) url += `keyword=${key}&`;
    history.push(url);
  };

  useEffect(() => {
    const depts = departments?.data?.result?.departments;
    const deptId = Helpers.getValueFromUrl('tenantDepartmentId')?.toString() || Helpers.getTenantDepartmentId(user)?.toString();
    setKeyword(Helpers.getValueFromUrl('keyword'));
    setSearchKeyword(Helpers.getValueFromUrl('keyword'));
    setTenantDepartmentId(deptId);
    setParentLocationId(Helpers.getValueFromUrl('parentLocationId'));
    const currentDeptInfo = depts?.find((item) => item?.tenant_department_id == tenantDepartmentId);
    setTenantDepartmentName(currentDeptInfo?.department_info?.department_name);
    if (deptId) {
      getInventoryLocations(deptId, Helpers.getValueFromUrl('keyword') || '', Helpers.getValueFromUrl('parentLocationId') || 'null');
    }
  }, [departments]);

  useEffect(() => {
    pushParamsToUrl(tenantDepartmentId, parentLocationId, keyword);
    if (tenantDepartmentId) {
      getInventoryLocations(tenantDepartmentId, keyword, parentLocationId || 'null');
    }
  }, [keyword, tenantDepartmentId, parentLocationId]);

  /**
   *
   */
  const addLocation = () => {
    setFormSubmitted(true);
    if (sectionName.trim()) {
      const payload = {
        tenant_department_id: tenantDepartmentId,
        inventory_location_name: sectionName,
        parent_id: parentLocationId || null,
        inventory_location_id: selectedLocation?.inventory_location_id,
        org_id: user?.tenant_info?.org_id,
        is_active: sectionActive,
        is_storable: sectionStorable,
      };
      if (payload?.inventory_location_id) {
        updateInventoryLocation(payload, () => {
          getInventoryLocations(tenantDepartmentId, keyword, parentLocationId || 'null');
          setShowNewLocationDrawer(false);
          setSectionName('');
          setFormSubmitted(false);
        });
      } else {
        createInventoryLocation(payload, () => {
          getInventoryLocations(tenantDepartmentId, keyword, parentLocationId || 'null');
          setShowNewLocationDrawer(false);
          setSectionName('');
          setFormSubmitted(false);
        });
      }
    }
  };

  const getPath = () => {
    const parentPath = inventoryLocations?.parent;
    if (parentPath) {
      const breadCrumb = (
        <Fragment>
          {
            parentPath?.map((path) => (
              <Breadcrumb.Item onClick={() => {
                setParentLocationId(path?.inventory_location_id);
                setKeyword('');
              }}
              >
                {path?.inventory_location_name}
              </Breadcrumb.Item>
            ))
          }
        </Fragment>
      );
      return breadCrumb;
    }
    return <Fragment />;
  };

  return (
    <Fragment>
      <div className="warehouse-setup__wrapper">
        <div className="warehouse-setup__header">
          <div className="section-search" style={{ width: '300px', marginRight: '10px' }}>
            <div className="section-search-bar__wrapper section-search__container">
              <div className="section-search-bar">
                <input
                  value={searchKeyword}
                  placeholder="Search location name.."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setKeyword(searchKeyword);
                    }
                  }}
                  onChange={(event) => setSearchKeyword(event.target.value)}
                />
                <H3Image
                  src={searchIcon}
                  className="section-search-bar__icon"
                  alt="search"
                  onClick={() => {
                    setKeyword(searchKeyword);
                  }}
                />
              </div>
            </div>
            {keyword && keyword.length > 0 && (
              <div className="search-bar__search-container__clear-button" style={{ marginTop: '15px' }}>
                <H3Text
                  text={(
                    <React.Fragment>
                      Showing search results for&nbsp;
                      <i>
                        <b>
                          {`'${keyword}'`}
                        </b>
                      </i>
                    </React.Fragment>
                  )}
                  className="section-search-keyword"
                />
                <H3Text
                  text="clear"
                  className="search-bar__clear-button"
                  onClick={() => {
                    setSearchKeyword('');
                    setKeyword('');
                    setCurrentPage(1);
                  }}
                />
              </div>
            )}
          </div>
          <SelectDepartment
            hideTitle
            selectedDepartment={tenantDepartmentId ? parseInt(tenantDepartmentId) : null}
            onChange={(value) => {
              setTenantDepartmentId(value?.tenant_department_id);
              setParentLocationId(null);
              setKeyword(null);
            }}
            tenentLevelDepartment
            emptyNotAllowed
            customStyle={{
              padding: '0px 3px',
              width: '150px',
            }}
            loading={getInventoryLocationsLoading}
          />
          <H3Button
            buttonType={defaultButtonTypes.BLUE_ROUNDED}
            text="+ Add Section"
            onClick={() => {
              setShowNewLocationDrawer(true);
              setSectionActive(true);
              setSelectedLocation(null);
              setSectionName('');
            }}
            customClass="warehouse-setup__header-button"
          />
        </div>
      </div>

      <div className="warehouse-setup__location-path">
        <Breadcrumb>
          <Breadcrumb.Item onClick={() => {
            setParentLocationId(null);
            setKeyword('');
          }}
          >
            <HomeFilled />
            &nbsp;
            {tenantDepartmentName}
          </Breadcrumb.Item>
          {getPath()}
        </Breadcrumb>
      </div>
      <div className="warehouse-setup__locations">
        {getInventoryLocationsLoading ? (
          <div className="warehouse-setup__locations-loading">
            <div className="loadingBlock warehouse-setup__locations-loading-block" />
            <div className="loadingBlock warehouse-setup__locations-loading-block" />
            <div className="loadingBlock warehouse-setup__locations-loading-block" />
            <div className="loadingBlock warehouse-setup__locations-loading-block" />
          </div>
        ) : (
          <Fragment>
            {
            inventoryLocations?.inventory_location?.length ? (
              <div className="ant-row">
                {inventoryLocations?.inventory_location?.map((item) => (
                  <div key={item?.inventory_location_id} className="ant-col-md-5">
                    <div className="warehouse-setup__locations-item">
                      <div className="warehouse-setup__locations-item__name-wrapper">
                        <H3Text
                          text={item?.inventory_location_name}
                          className="warehouse-setup__locations-item__name"
                          onClick={() => setParentLocationId(item?.inventory_location_id)}
                        />
                        <div
                          className={item?.is_default_location ? 'warehouse-setup__locations-item__disable-edit-icon' : 'warehouse-setup__locations-item__edit-icon'}
                          onClick={() => {
                            if (!item?.is_default_location) {
                              setSelectedLocation(item);
                              setShowNewLocationDrawer(true);
                              setSectionName(item?.inventory_location_name);
                              setSectionActive(item?.is_active);
                              setSectionStorable(item?.is_storable);
                            }
                          }}
                        >
                          <EditOutlined />
                        </div>
                      </div>
                      <div style={{ display: 'flex' }}>
                        <div
                          className={`warehouse-setup__locations-item__status ${item?.is_active ? 'warehouse-setup__locations-item__active' : ''}`}
                        >
                          {item?.is_active ? 'Active' : 'Inactive'}
                        </div>
                        {item?.is_storable && (
                          <div
                            className="warehouse-setup__locations-item__storable"
                          >
                            Storable
                          </div>
                        )}
                        <H3Text text={`${item?.count} Sections`} className="warehouse-setup__locations-item__count" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="warehouse-setup__locations-empty">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No sections have been setup for this location."
                />
              </div>
            )
            }
          </Fragment>
        )}
      </div>
      <Drawer
        open={showNewLocationDrawer}
        width="360px"
        mask
        onClose={() => {
          setShowNewLocationDrawer(false);
          setSelectedLocation(null);
          setSectionName('');
        }}
        destroyOnClose
      >
        <div className="custom-drawer__header-wrapper">
          <div className="custom-drawer__header" style={{ width: '315px' }}>
            <H3Text text="Add New Section" className="custom-drawer__title" />
            <H3Image src={closeIcon} className="custom-drawer__close-icon" onClick={() => setShowNewLocationDrawer(false)} />
          </div>
        </div>
        <H3FormInput
          name="section name"
          type="text"
          containerClassName="orgInputContainer"
          labelClassName="orgFormLabel"
          inputClassName="orgFormInput"
          placeholder=""
          onChange={(event) => setSectionName(event.target.value)}
          value={sectionName}
          label="Section Name"
          maxlength="50"
          disabled={createInventoryLocationLoading || updateInventoryLocationLoading}
          required
          showError={formSubmitted && !sectionName.trim()}
        />
        <div>
          <Checkbox onClick={() => setSectionStorable(!sectionStorable)} checked={sectionStorable}> Set this section as Storable</Checkbox>
        </div>
        <div>
          <Checkbox onClick={() => setSectionActive(!sectionActive)} checked={sectionActive}> Set this section as Active</Checkbox>
        </div>
        <div className="custom-drawer__footer">
          <div className="ant-col-md-6" style={{ marginTop: '0.5rem' }}>
            <H3Button
              buttonType={defaultButtonTypes.BLUE_ROUNDED}
              text={selectedLocation ? 'Update Section' : 'Add Section'}
              onClick={() => addLocation()}
              isLoading={createInventoryLocationLoading || updateInventoryLocationLoading}
              disabled={createInventoryLocationLoading || updateInventoryLocationLoading}
              style={{
                width: '200px',
                borderRadius: '5px',
                padding: '7px 0px',
                marginLeft: '0',
              }}
            />
          </div>
        </div>
      </Drawer>
    </Fragment>
  );
}

const mapStateToProps = ({ UserReducers, DepartmentReducers, InventoryLocationReducers }) => ({
  user: UserReducers.user,
  departments: DepartmentReducers.departments,
  getInventoryLocationsLoading: InventoryLocationReducers.getInventoryLocationsLoading,
  inventoryLocations: InventoryLocationReducers.inventoryLocations,
  createInventoryLocationLoading: InventoryLocationReducers.createInventoryLocationLoading,
  updateInventoryLocationLoading: InventoryLocationReducers.updateInventoryLocationLoading,
  deleteInventoryLocationLoading: InventoryLocationReducers.deleteInventoryLocationLoading,
});

const mapDispatchToProps = (dispatch) => ({
  getInventoryLocations: (tenantDepartmentId, inventoryLocationPath, parentLocationId) => dispatch(InventoryLocationActions.getInventoryLocations(tenantDepartmentId, inventoryLocationPath, parentLocationId)),
  createInventoryLocation: (payload, callback) => dispatch(InventoryLocationActions.createInventoryLocation(payload, callback)),
  updateInventoryLocation: (payload, callback) => dispatch(InventoryLocationActions.updateInventoryLocation(payload, callback)),
  deleteInventoryLocation: (payload, callback) => dispatch(InventoryLocationActions.deleteInventoryLocation(payload, callback)),
});

WarehouseSetup.propTypes = {
  user: PropTypes.any,
  getInventoryLocations: PropTypes.func,
  inventoryLocations: PropTypes.any,
  getInventoryLocationsLoading: PropTypes.bool,
  getInventoryLocationsSuccess: PropTypes.func,
  createInventoryLocation: PropTypes.func,
  createInventoryLocationLoading: PropTypes.bool,
  updateInventoryLocation: PropTypes.func,
  updateInventoryLocationLoading: PropTypes.bool,
  deleteInventoryLocation: PropTypes.func,
  deleteInventoryLocationLoading: PropTypes.bool,
  location: PropTypes.any,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(WarehouseSetup));
